// Named JS actions a SystemToast CTA can dispatch instead of navigating.
//
// A toast row whose `cta_link` is `action:<name>` runs the corresponding
// function here when the CTA button is clicked. Use this when a single
// click should *do* something (e.g. trigger a browser permission prompt)
// rather than navigating to a settings page.
//
// Add a new action by:
//   1. Writing a small handler here keyed by a stable, lowercase, dash-cased name.
//   2. Setting `cta_link = "action:<name>"` when creating the SystemToast.
//
// Keep handlers idempotent and side-effect-only — the toaster won't await
// them and won't surface errors. If the action needs follow-up UI, render
// that in response to the state change the action produced, not from here.
import { requestBrowserNotificationPermission } from "@/hooks/use-browser-notification-permission";

export type SystemToastActionName = "request-browser-notifications";

const ACTIONS: Record<SystemToastActionName, () => void | Promise<unknown>> = {
  "request-browser-notifications": () => requestBrowserNotificationPermission(),
};

export const SYSTEM_TOAST_ACTION_PREFIX = "action:";

export function isSystemToastAction(
  ctaLink: string | null | undefined,
): boolean {
  return !!ctaLink && ctaLink.startsWith(SYSTEM_TOAST_ACTION_PREFIX);
}

export function parseSystemToastAction(
  ctaLink: string,
): SystemToastActionName | null {
  if (!ctaLink.startsWith(SYSTEM_TOAST_ACTION_PREFIX)) return null;
  const name = ctaLink.slice(SYSTEM_TOAST_ACTION_PREFIX.length);
  // Use Object.hasOwn rather than `in` so inherited Object.prototype keys
  // (`toString`, `constructor`, etc.) don't get treated as valid actions.
  return Object.hasOwn(ACTIONS, name) ? (name as SystemToastActionName) : null;
}

export function runSystemToastAction(name: SystemToastActionName): void {
  // Fire-and-forget. The native browser prompt the action triggers is its
  // own user-visible feedback — we don't surface errors here. The rejection
  // still has to be absorbed, though: an uncaught one from a click handler
  // reports as an app-level error.
  void Promise.resolve(ACTIONS[name]()).catch(() => {});
}
